import { Group } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  IEmailABTestSettings,
  MailrailBaseEmailCampaign,
  TEmailABTestVariation,
} from '@feathr/blackbox';
import { Alert, CardV2 as Card, Fieldset, Input, SegmentedControl } from '@feathr/components';
import type { ICampaignValidationErrors } from '@feathr/extender/App/EventsPage/CampaignSummary';
import { getEmailABTestVersionLabel } from '@feathr/extender/utils/emailABTest';

import type { IGetEmailABTestVersionMailing } from '../../useMailrailABTest';
import MailrailMailingConfig from '../MailrailMailingConfig';
import CopyFromVersionAButton from './CopyFromVersionAButton';

export interface IMailrailABTestVersionTabsProps {
  abTest: IEmailABTestSettings;
  campaign: MailrailBaseEmailCampaign;
  disabled: boolean;
  getVersionMailing: IGetEmailABTestVersionMailing;
  hasUnsavedChanges: boolean;
  onChangeABTest: (abTest: IEmailABTestSettings) => void;
  onCopyVersionA: () => Promise<void>;
  validationErrors: ICampaignValidationErrors;
}

function MailrailABTestVersionTabs({
  abTest,
  campaign,
  disabled,
  getVersionMailing,
  hasUnsavedChanges,
  onChangeABTest,
  onCopyVersionA,
  validationErrors,
}: Readonly<IMailrailABTestVersionTabsProps>): JSX.Element {
  const { t } = useTranslation();
  const [version, setVersion] = useState<TEmailABTestVariation>('a');

  const mailing = getVersionMailing(version);
  const isVersionA = version === 'a';
  const helpText = t('Give this version a name to help you identify it.');

  function handleChangeVersion(value: string): void {
    setVersion(value === 'b' ? 'b' : 'a');
  }

  function handleChangeVersionAName(versionAName?: string): void {
    onChangeABTest({ ...abTest, version_a_name: versionAName });
  }

  const versionSwitcher = (
    <Group justify={'space-between'}>
      <SegmentedControl
        data={[
          { label: getEmailABTestVersionLabel('a', t), value: 'a' },
          { label: getEmailABTestVersionLabel('b', t), value: 'b' },
        ]}
        name={'ab_test_version'}
        onChange={handleChangeVersion}
        value={version}
      />
      {!isVersionA && mailing && (
        <CopyFromVersionAButton
          disabled={disabled}
          hasUnsavedChanges={hasUnsavedChanges}
          onCopy={onCopyVersionA}
        />
      )}
    </Group>
  );

  if (!mailing) {
    return (
      <Card>
        <Card.Header title={t('Email Information')} />
        <Card.Content addVerticalGap={true}>
          {versionSwitcher}
          <Alert title={t('Save your campaign to start editing Version B.')} type={'info'} />
        </Card.Content>
      </Card>
    );
  }

  return (
    <MailrailMailingConfig
      campaign={campaign}
      disabled={disabled}
      key={version}
      mailing={mailing}
      validationErrors={isVersionA ? validationErrors : {}}
      variant={'combined'}
    >
      {versionSwitcher}
      <Fieldset>
        {isVersionA ? (
          <Input
            disabled={disabled}
            helpText={helpText}
            label={t('Version name')}
            name={'version_a_name'}
            onChange={handleChangeVersionAName}
            optional={true}
            type={'text'}
            value={abTest.version_a_name ?? ''}
          />
        ) : (
          <Input
            attribute={'name'}
            disabled={disabled}
            helpText={helpText}
            label={t('Version name')}
            model={mailing}
            name={'version_b_name'}
            optional={true}
            type={'text'}
          />
        )}
      </Fieldset>
    </MailrailMailingConfig>
  );
}

export default observer(MailrailABTestVersionTabs);
